import React, { useState, useEffect } from 'react'
import { Button } from '../components/ui/Button'

interface ScraperStatus {
  running: boolean
  paused: boolean
  pid: number | null
  start_time: string | null
  mode: string | null
}

interface OutputFile {
  name: string
  size: number
  modified: number
}

interface ScraperOutputs {
  json: OutputFile[]
  excel: OutputFile[]
  reports: OutputFile[]
}

const ScraperPage: React.FC = () => {
  const [scraperStatus, setScraperStatus] = useState<ScraperStatus>({
    running: false,
    paused: false,
    pid: null,
    start_time: null,
    mode: null
  })
  const [outputs, setOutputs] = useState<ScraperOutputs>({
    json: [],
    excel: [],
    reports: []
  })
  const [isLoading, setIsLoading] = useState(false)
  const [selectedMode, setSelectedMode] = useState('test')
  const [maxProjects, setMaxProjects] = useState('')
  const [countries, setCountries] = useState('')
  const [maxWorkers, setMaxWorkers] = useState('2')
  const [maxConcurrency, setMaxConcurrency] = useState('1')
  const [batchSize, setBatchSize] = useState('50')
  const [logs, setLogs] = useState<string[]>([])
  const [isLoadingOutputs, setIsLoadingOutputs] = useState(false)
  
  // Progress tracking state
  const [progress, setProgress] = useState<any>(null)
  const [progressInterval, setProgressInterval] = useState<NodeJS.Timeout | null>(null)
  
  // Settings visibility state
  const [showSettings, setShowSettings] = useState(false)

  // Fetch scraper status
  const fetchStatus = async () => {
    try {
      const response = await fetch('/api/v1/scraper/status', {
        credentials: 'include'
      })
      const result = await response.json()
      if (result.success) {
        setScraperStatus(result.data)
      }
    } catch (error) {
      console.error('Failed to fetch scraper status:', error)
    }
  }

  // Fetch scraper progress
  const fetchProgress = async () => {
    try {
      const response = await fetch('/api/v1/scraper/progress', {
        credentials: 'include'
      })
      const result = await response.json()
      if (result.success) {
        setProgress(result.data)
      }
    } catch (error) {
      console.error('Failed to fetch scraper progress:', error)
    }
  }

  // Fetch output files
  const fetchOutputs = async () => {
    setIsLoadingOutputs(true)
    try {
      const response = await fetch('/api/v1/scraper/outputs', {
        credentials: 'include'
      })
      const result = await response.json()
      if (result.success) {
        setOutputs(result.data)
      }
    } catch (error) {
      console.error('Failed to fetch outputs:', error)
    } finally {
      setIsLoadingOutputs(false)
    }
  }

  // Start scraper
  const startScraper = async () => {
    setIsLoading(true)
    try {
      const requestBody: any = { mode: selectedMode }
      
      if (maxProjects) {
        requestBody.max_projects = parseInt(maxProjects)
      }
      
      if (countries) {
        requestBody.countries = countries.split(',').map(c => c.trim())
      }
      
      if (maxWorkers) {
        requestBody.max_workers = parseInt(maxWorkers)
      }
      
      if (maxConcurrency) {
        requestBody.max_concurrency = parseInt(maxConcurrency)
      }
      
      if (batchSize) {
        requestBody.batch_size = parseInt(batchSize)
      }

      const response = await fetch('/api/v1/scraper/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      })

      const result = await response.json()
      if (result.success) {
        setScraperStatus(result.data)
        // Start polling for status updates
        startStatusPolling()
      } else {
        alert(`Failed to start scraper: ${result.error}`)
      }
    } catch (error) {
      console.error('Failed to start scraper:', error)
      alert('Failed to start scraper')
    } finally {
      setIsLoading(false)
    }
  }

  // Stop scraper
  const stopScraper = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/v1/scraper/stop', {
        method: 'POST',
        credentials: 'include'
      })

      const result = await response.json()
      if (result.success) {
        setScraperStatus({
          running: false,
          paused: false,
          pid: null,
          start_time: null,
          mode: null
        })
        stopStatusPolling()
      } else {
        alert(`Failed to stop scraper: ${result.error}`)
      }
    } catch (error) {
      console.error('Failed to stop scraper:', error)
      alert('Failed to stop scraper')
    } finally {
      setIsLoading(false)
    }
  }

  // Pause scraper
  const pauseScraper = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/v1/scraper/pause', {
        method: 'POST',
        credentials: 'include'
      })

      const result = await response.json()
      if (result.success) {
        setScraperStatus(result.data)
      } else {
        alert(`Failed to pause scraper: ${result.error}`)
      }
    } catch (error) {
      console.error('Failed to pause scraper:', error)
      alert('Failed to pause scraper')
    } finally {
      setIsLoading(false)
    }
  }

  // Resume scraper
  const resumeScraper = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/v1/scraper/resume', {
        method: 'POST',
        credentials: 'include'
      })

      const result = await response.json()
      if (result.success) {
        setScraperStatus(result.data)
      } else {
        alert(`Failed to resume scraper: ${result.error}`)
      }
    } catch (error) {
      console.error('Failed to resume scraper:', error)
      alert('Failed to resume scraper')
    } finally {
      setIsLoading(false)
    }
  }

  // Status polling
  let statusInterval: NodeJS.Timeout | null = null

  const startStatusPolling = () => {
    statusInterval = setInterval(() => {
      fetchStatus()
      fetchProgress()  // Also fetch progress
    }, 2000) // Poll every 2 seconds
  }

  const stopStatusPolling = () => {
    if (statusInterval) {
      clearInterval(statusInterval)
      statusInterval = null
    }
  }

  // Download file
  const downloadFile = async (fileType: string, filename: string) => {
    try {
      const response = await fetch(`/api/v1/scraper/download/${fileType}/${filename}`, {
        credentials: 'include'
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } else {
        alert('Failed to download file')
      }
    } catch (error) {
      console.error('Failed to download file:', error)
      alert('Failed to download file')
    }
  }

  const deleteFile = async (fileType: string, filename: string) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) {
      return
    }

    try {
      const response = await fetch(`/api/v1/scraper/outputs/${fileType}/${filename}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (response.ok) {
        // Refresh the outputs list
        await fetchOutputs()
        alert('File deleted successfully')
      } else {
        const error = await response.json()
        alert(`Failed to delete file: ${error.detail || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Failed to delete file:', error)
      alert('Failed to delete file')
    }
  }

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  // Format date
  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString()
  }

  // Initialize
  useEffect(() => {
    fetchStatus()
    fetchOutputs()
    fetchProgress()  // Always fetch progress on load
    
    // Check if scraper is running and start polling
    const checkInitialStatus = async () => {
      await fetchStatus()
      await fetchProgress()
      // The status will be updated, and we'll start polling if needed
    }
    
    checkInitialStatus()

    return () => {
      stopStatusPolling()
    }
  }, [])

  // Start polling when scraper starts running
  useEffect(() => {
    if (scraperStatus.running && !statusInterval) {
      startStatusPolling()
    } else if (!scraperStatus.running && statusInterval) {
      stopStatusPolling()
    }
  }, [scraperStatus.running])

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">MiningHub Scraper</h1>
        <p className="text-gray-600">
          Run the MiningHub data processor to extract mining project data with company relationships and geocoding.
        </p>
      </div>

      {/* Scraper Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Scraper Controls</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              <span className="mr-2">⚙️</span>
              {showSettings ? 'Hide Settings' : 'Show Settings'}
              <span className="ml-2">{showSettings ? '▲' : '▼'}</span>
            </button>
          </div>
        </div>
        
        {/* About the Scraper */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            The MiningHub Scraper is a cloud-native data processing system that extracts mining project data 
            with authoritative company relationships and geocoding enrichment. Choose your processing mode and 
            resource allocation to optimize performance for your needs.
          </p>
        </div>

        {/* Progress Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Progress
            {!scraperStatus.running && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                {progress?.status === 'completed' ? '(Last Run Completed)' :
                 progress?.status === 'cancelled' ? '(Last Run Cancelled)' : 
                 '(No Active Process)'}
              </span>
            )}
          </h3>
          
          {/* Status Information */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-700">Status:</span>
                <div className="flex items-center mt-1">
                  <div className={`w-3 h-3 rounded-full mr-2 ${
                    scraperStatus.running && !scraperStatus.paused ? 'bg-green-500' : 
                    scraperStatus.paused ? 'bg-yellow-500' : 'bg-gray-400'
                  }`}></div>
                  <span className={`font-medium ${
                    scraperStatus.running && !scraperStatus.paused ? 'text-green-600' : 
                    scraperStatus.paused ? 'text-yellow-600' : 'text-gray-600'
                  }`}>
                    {scraperStatus.running && !scraperStatus.paused ? 'Running' : 
                     scraperStatus.paused ? 'Paused' : 'Stopped'}
                  </span>
                </div>
              </div>
              
              {scraperStatus.pid && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Process ID:</span>
                  <div className="mt-1 text-gray-900 font-mono">{scraperStatus.pid}</div>
                </div>
              )}
              
              {scraperStatus.mode && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Mode:</span>
                  <div className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      scraperStatus.mode === 'production' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {scraperStatus.mode}
                    </span>
                  </div>
                </div>
              )}
              
              {scraperStatus.start_time && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Started:</span>
                  <div className="mt-1 text-gray-900 text-sm">
                    {new Date(scraperStatus.start_time).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>
            
            {/* Overall Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                <span className="text-sm text-gray-600">{Math.round(progress?.overall_progress || 0)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-green-500 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress?.overall_progress || 0}%` }}
                ></div>
              </div>
            </div>

            {/* Current Phase Progress */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Current Phase: <span className="capitalize text-blue-600">{progress?.current_phase || 'not_started'}</span>
                </span>
                <span className="text-sm text-gray-600">{Math.round(progress?.phase_progress || 0)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress?.phase_progress || 0}%` }}
                ></div>
              </div>
            </div>

            {/* Stage Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Discovery Stage */}
              <div className={`p-3 rounded-lg border-2 ${
                progress?.current_phase === 'discovery' ? 'border-blue-500 bg-blue-50' :
                (progress?.overall_progress || 0) > 10 ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'
              }`}>
                <h4 className="font-semibold text-sm mb-2">
                  {(progress?.overall_progress || 0) > 10 ? '✅' : progress?.current_phase === 'discovery' ? '🔄' : '⏳'} Discovery
                </h4>
                {(progress?.countries_total || 0) > 0 && (
                  <p className="text-xs text-gray-600">
                    Countries: {progress?.countries_completed || 0}/{progress?.countries_total || 0}
                  </p>
                )}
                {(progress?.total_gids || 0) > 0 && (
                  <p className="text-xs text-gray-600">
                    Found: {(progress?.total_gids || 0).toLocaleString()} projects
                  </p>
                )}
                {!progress && (
                  <p className="text-xs text-gray-600">Ready to discover projects</p>
                )}
              </div>

              {/* Assembly Stage */}
              <div className={`p-3 rounded-lg border-2 ${
                progress?.current_phase === 'assembly' ? 'border-blue-500 bg-blue-50' :
                (progress?.overall_progress || 0) > 95 ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'
              }`}>
                <h4 className="font-semibold text-sm mb-2">
                  {(progress?.overall_progress || 0) > 95 ? '✅' : progress?.current_phase === 'assembly' ? '🔄' : '⏳'} Assembly
                </h4>
                {(progress?.projects_total || 0) > 0 && (
                  <p className="text-xs text-gray-600">
                    Projects: {(progress?.projects_completed || 0).toLocaleString()}/{(progress?.projects_total || 0).toLocaleString()}
                  </p>
                )}
                {(progress?.total_batches || 0) > 0 && (
                  <p className="text-xs text-gray-600">
                    Batches: {progress?.current_batch || 0}/{progress?.total_batches || 0}
                  </p>
                )}
                {(progress?.projects_failed || 0) > 0 && (
                  <p className="text-xs text-red-600">
                    Failed: {(progress?.projects_failed || 0).toLocaleString()}
                  </p>
                )}
                {!progress && (
                  <p className="text-xs text-gray-600">Ready to process projects</p>
                )}
              </div>

              {/* Export Stage */}
              <div className={`p-3 rounded-lg border-2 ${
                progress?.current_phase === 'export' ? 'border-blue-500 bg-blue-50' :
                progress?.current_phase === 'completed' ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'
              }`}>
                <h4 className="font-semibold text-sm mb-2">
                  {progress?.current_phase === 'completed' ? '✅' : progress?.current_phase === 'export' ? '🔄' : '⏳'} Export
                </h4>
                <p className="text-xs text-gray-600">
                  {progress?.current_phase === 'completed' ? 'Files ready' : 
                   progress?.current_phase === 'export' ? 'Generating files' : 
                   !progress ? 'Ready to export' : 'Waiting'}
                </p>
              </div>
            </div>

            {/* Current Activity */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Current Activity:</span> {progress?.current_activity || 'No active process'}
              </p>
              {progress?.estimated_completion && (
                <p className="text-xs text-gray-600 mt-1">
                  <span className="font-medium">Estimated Completion:</span> {new Date(progress.estimated_completion).toLocaleString()}
                </p>
              )}
              {!progress && (
                <p className="text-xs text-gray-600 mt-1">
                  Start the scraper to see real-time progress tracking
                </p>
              )}
            </div>
          </div>

        {/* Scraper Settings */}
        {!scraperStatus.running && showSettings && (
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-amber-900 mb-3">⚙️ Scraper Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Processing Mode
                  </label>
                  <select
                    value={selectedMode}
                    onChange={(e) => setSelectedMode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="test">Test (Australia, ~30 seconds)</option>
                    <option value="production">Production (All countries, 2-6 hours)</option>
                  </select>
                </div>
              </div>
              
              <div className="border-t border-amber-300 pt-4">
                <h5 className="text-sm font-semibold text-amber-900 mb-3">⚡ Resource Controls (Prevent System Freeze)</h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Workers
                    <span className="text-xs text-gray-500 block">Concurrent threads (1-20)</span>
                  </label>
                  <select
                    value={maxWorkers}
                    onChange={(e) => setMaxWorkers(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">1 (Conservative)</option>
                    <option value="2">2 (Safe)</option>
                    <option value="4">4 (Balanced)</option>
                    <option value="6">6 (Moderate)</option>
                    <option value="8">8 (Aggressive)</option>
                    <option value="10">10 (High Performance)</option>
                    <option value="12">12 (Very High)</option>
                    <option value="15">15 (Extreme)</option>
                    <option value="20">20 (Maximum)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Browser Concurrency
                    <span className="text-xs text-gray-500 block">Parallel browsers (1-20)</span>
                  </label>
                  <select
                    value={maxConcurrency}
                    onChange={(e) => setMaxConcurrency(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">1 (Conservative)</option>
                    <option value="2">2 (Safe)</option>
                    <option value="4">4 (Balanced)</option>
                    <option value="6">6 (Moderate)</option>
                    <option value="8">8 (Aggressive)</option>
                    <option value="10">10 (High Performance)</option>
                    <option value="12">12 (Very High)</option>
                    <option value="15">15 (Extreme)</option>
                    <option value="20">20 (Maximum)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Batch Size
                    <span className="text-xs text-gray-500 block">Projects per batch</span>
                  </label>
                  <input
                    type="number"
                    value={batchSize}
                    onChange={(e) => setBatchSize(e.target.value)}
                    min="10"
                    max="200"
                    placeholder="50"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="mt-3 text-xs text-amber-800">
                <strong>Your instance has 3.7GB RAM and 2 vCPUs.</strong> Recommended: 4-8 workers, 4-8 browsers. 
                Higher settings may impact performance. Monitor system resources.
              </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-4">
          {!scraperStatus.running ? (
            <Button
              onClick={startScraper}
              loading={isLoading}
              disabled={isLoading}
              variant="success"
            >
              Start Scraper
            </Button>
          ) : (
            <>
              {scraperStatus.paused ? (
                <Button
                  onClick={resumeScraper}
                  loading={isLoading}
                  disabled={isLoading}
                  variant="success"
                >
                  Resume Scraper
                </Button>
              ) : (
                <Button
                  onClick={pauseScraper}
                  loading={isLoading}
                  disabled={isLoading}
                  variant="warning"
                >
                  Pause Scraper
                </Button>
              )}
              
              <Button
                onClick={stopScraper}
                loading={isLoading}
                disabled={isLoading}
                variant="error"
                className="font-semibold"
              >
                🛑 Emergency Stop
              </Button>
            </>
          )}
          
          <Button
            onClick={fetchOutputs}
            loading={isLoadingOutputs}
            disabled={isLoadingOutputs}
            variant="secondary"
          >
            Refresh Outputs
          </Button>
        </div>

      </div>

      {/* Output Files */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Output Files</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* JSON Files */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">JSON Files (CRM Ready)</h3>
            <div className="space-y-2">
              {outputs.json.length === 0 ? (
                <p className="text-gray-500 text-sm">No JSON files available</p>
              ) : (
                outputs.json.map((file, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)} • {formatDate(file.modified)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => downloadFile('json', file.name)}
                          variant="secondary"
                          className="text-xs px-2 py-1"
                        >
                          Download
                        </Button>
                        <button
                          onClick={() => deleteFile('json', file.name)}
                          className="text-red-500 hover:text-red-700 text-lg font-bold px-1 py-1 rounded hover:bg-red-50 transition-colors"
                          title="Delete file"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Excel Files */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Excel Reports</h3>
            <div className="space-y-2">
              {outputs.excel.length === 0 ? (
                <p className="text-gray-500 text-sm">No Excel files available</p>
              ) : (
                outputs.excel.map((file, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)} • {formatDate(file.modified)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => downloadFile('excel', file.name)}
                          variant="secondary"
                          className="text-xs px-2 py-1"
                        >
                          Download
                        </Button>
                        <button
                          onClick={() => deleteFile('excel', file.name)}
                          className="text-red-500 hover:text-red-700 text-lg font-bold px-1 py-1 rounded hover:bg-red-50 transition-colors"
                          title="Delete file"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Report Files */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Processing Reports</h3>
            <div className="space-y-2">
              {outputs.reports.length === 0 ? (
                <p className="text-gray-500 text-sm">No report files available</p>
              ) : (
                outputs.reports.map((file, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)} • {formatDate(file.modified)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => downloadFile('reports', file.name)}
                          variant="secondary"
                          className="text-xs px-2 py-1"
                        >
                          Download
                        </Button>
                        <button
                          onClick={() => deleteFile('reports', file.name)}
                          className="text-red-500 hover:text-red-700 text-lg font-bold px-1 py-1 rounded hover:bg-red-50 transition-colors"
                          title="Delete file"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}

export default ScraperPage
